import React from "react";
import { Landmark, Search, MapPin, ExternalLink, List, Map as MapIcon } from "lucide-react";
import { motion } from "framer-motion";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import MuseumsMap from "../components/site/MuseumsMap";
import FullImage from "../components/site/FullImage";

const fold = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function Museums() {
  useSEO(
    "Yorùbá Museums & Cultural Institutions",
    "A directory of the major museums, galleries, heritage sites and cultural institutions that preserve Yorùbá art, history and heritage — from the National Museum Lagos and the Museum of Ifẹ̀ Antiquities to the UNESCO Oṣun-Òṣogbo Sacred Grove."
  );

  const [museums, setMuseums] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [term, setTerm] = React.useState("");
  const [type, setType] = React.useState("all");
  const [view, setView] = React.useState("list");

  React.useEffect(() => {
    api
      .get("/museums")
      .then((r) => setMuseums(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const types = React.useMemo(() => {
    const seen = [];
    museums.forEach((m) => {
      if (m.type && !seen.includes(m.type)) seen.push(m.type);
    });
    return ["all", ...seen];
  }, [museums]);

  const filtered = museums.filter((m) => {
    if (type !== "all" && m.type !== type) return false;
    if (term.trim()) {
      const q = fold(term.trim());
      return (
        fold(m.name).includes(q) ||
        fold(m.location).includes(q) ||
        fold(m.description).includes(q) ||
        fold(m.type).includes(q)
      );
    }
    return true;
  });

  return (
    <div className="bg-night text-cream min-h-screen">
      {/* HERO */}
      <header className="max-w-5xl mx-auto px-6 pt-24 pb-10">
        <Reveal>
          <div className="flex items-center gap-2 mb-4">
            <Landmark className="w-4 h-4 text-gold" />
            <p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Culture Keepers · Ilé Ìṣẹ̀ǹbáyé</p>
          </div>
          <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="museums-title">
            Museums & Cultural Institutions
          </h1>
          <p className="text-cream/70 text-lg leading-relaxed max-w-2xl">
            The great museums, galleries, heritage sites and cultural centres that guard Yorùbá
            art, history and memory — from ancient Ifẹ̀ bronzes and Esie's soapstone figures to the
            sacred groves of Òṣogbo.
          </p>
        </Reveal>
      </header>

      {/* CONTROLS */}
      <div className="max-w-5xl mx-auto px-6 pb-8 space-y-5">
        <Reveal>
          <div className="relative">
            <Search className="w-4 h-4 text-cream/40 absolute left-5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search an institution, city or type…"
              data-testid="museums-search-input"
              className="w-full rounded-full bg-night-surface border border-cream/20 pl-12 pr-6 py-3.5 text-cream placeholder:text-cream/40 focus:outline-none focus:border-gold transition-colors"
            />
          </div>
        </Reveal>
        <Reveal delay={0.05}>
          <div className="flex flex-wrap items-center gap-2">
            {types.map((t) => (
              <button
                key={t}
                onClick={() => setType(t)}
                data-testid={`museums-type-${fold(t).replace(/\s+/g, "-")}`}
                className={`text-sm rounded-full px-5 py-2 border transition-colors ${
                  type === t
                    ? "bg-gold text-night border-gold font-semibold"
                    : "border-cream/20 text-cream/75 hover:border-gold hover:text-gold"
                }`}
              >
                {t === "all" ? "All Types" : t}
              </button>
            ))}
            <span className="ml-auto self-center text-sm text-cream/45" data-testid="museums-count">
              {filtered.length} {filtered.length === 1 ? "institution" : "institutions"}
            </span>
            {/* List / Map view toggle */}
            <div className="inline-flex rounded-full border border-cream/20 p-1">
              <button
                onClick={() => setView("list")}
                data-testid="museums-view-list"
                className={`inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-1.5 transition-colors ${view === "list" ? "bg-gold text-night font-semibold" : "text-cream/70 hover:text-gold"}`}
              >
                <List className="w-4 h-4" /> List
              </button>
              <button
                onClick={() => setView("map")}
                data-testid="museums-view-map"
                className={`inline-flex items-center gap-1.5 text-sm rounded-full px-4 py-1.5 transition-colors ${view === "map" ? "bg-gold text-night font-semibold" : "text-cream/70 hover:text-gold"}`}
              >
                <MapIcon className="w-4 h-4" /> Map
              </button>
            </div>
          </div>
        </Reveal>
      </div>

      {/* LIST / MAP */}
      <div className="max-w-5xl mx-auto px-6 pb-24">
        {loading ? (
          <p className="text-cream/50 py-16 text-center">Loading institutions…</p>
        ) : view === "map" ? (
          <Reveal>
            <MuseumsMap museums={filtered} />
            <p className="text-center text-xs text-cream/40 mt-3">Tap a gold pin to see the institution · scroll-zoom disabled — use the +/− controls</p>
          </Reveal>
        ) : filtered.length === 0 ? (
          <p className="text-cream/50 py-16 text-center" data-testid="museums-empty">
            No institutions match your search yet.
          </p>
        ) : (
          <div className="grid gap-5 md:grid-cols-2">
            {filtered.map((m, i) => (
              <Reveal key={m.id} delay={(i % 4) * 0.05}>
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.25 }}
                  className="h-full flex flex-col border border-cream/12 rounded-2xl bg-night-surface overflow-hidden"
                  data-testid={`museum-card-${m.id}`}
                >
                  {m.image && (
                    <FullImage src={m.image} alt={m.name} className="h-44">
                      <div className="absolute inset-0 z-[2]" style={{ background: "linear-gradient(180deg, rgba(18,16,14,0.10) 0%, rgba(18,16,14,0.85) 100%)" }} />
                      <span className="absolute top-3 left-3 z-[3] inline-flex items-center gap-1.5 rounded-full bg-gold/90 text-night text-[11px] font-semibold px-3 py-1">
                        {m.type}
                      </span>
                    </FullImage>
                  )}
                  <div className="flex flex-col flex-1 p-7">
                    <h3 className="font-serif text-2xl text-gold leading-snug mb-2">{m.name}</h3>
                    {m.location && (
                      <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-gold-soft mb-4">
                        <MapPin className="w-3.5 h-3.5" /> {m.location}
                      </p>
                    )}
                    {m.description && (
                      <p className="text-cream/70 text-[15px] leading-relaxed mb-5 flex-1">{m.description}</p>
                    )}
                    {m.website && (
                      <a
                        href={m.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`museum-website-${m.id}`}
                        className="inline-flex items-center gap-2 self-start text-sm font-semibold text-gold hover:text-gold-light transition-colors mt-auto"
                      >
                        Visit website <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <Newsletter />
    </div>
  );
}
